import React, { useEffect, useRef } from 'react';
import { Animated, StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

export interface UnderConstructionScreenProps {
    eyebrow: string;
    title: string;
    subtitle: string;
    icon: string;
    accentColor?: string;
    features?: { icon: string; title: string; description: string }[];
    contactPrompt?: string;
    contactEmail?: string;
}

const DEFAULT_FEATURES = [
    {
        icon: 'clock-fast',
        title: 'Coming soon',
        description: 'We are building something great. This page will be live soon with helpful resources and information.',
    },
    {
        icon: 'hammer-wrench',
        title: 'Under active development',
        description: 'Our team is actively working on the content for this section. Check back for updates.',
    },
    {
        icon: 'bell-ring-outline',
        title: 'Stay in the loop',
        description: 'Follow the Announcements page to get notified when new sections of the site go live.',
    },
];

const UnderConstructionScreen: React.FC<UnderConstructionScreenProps> = ({
    eyebrow,
    title,
    subtitle,
    icon,
    accentColor = '#1D4ED8',
    features = DEFAULT_FEATURES,
    contactPrompt,
    contactEmail,
}) => {
    const pulseAnim = useRef(new Animated.Value(1)).current;
    const floatAnim = useRef(new Animated.Value(0)).current;
    const width = typeof window !== 'undefined' ? window.innerWidth : 0;
    const pageHorizontalPadding = width >= 1200 ? 100 : 24;
    const isWide = width >= 900;
    const heroTitleStyle = StyleSheet.flatten(styles.heroTitle);

    useEffect(() => {
        Animated.loop(
            Animated.sequence([
                Animated.timing(pulseAnim, { toValue: 1.08, duration: 1600, useNativeDriver: true }),
                Animated.timing(pulseAnim, { toValue: 1, duration: 1600, useNativeDriver: true }),
            ])
        ).start();

        Animated.loop(
            Animated.sequence([
                Animated.timing(floatAnim, { toValue: -8, duration: 2000, useNativeDriver: true }),
                Animated.timing(floatAnim, { toValue: 0, duration: 2000, useNativeDriver: true }),
            ])
        ).start();
    }, []);

    return (
        <View style={[styles.page, styles.pageContent, { paddingHorizontal: pageHorizontalPadding }]}>
            <View style={styles.container}>

                {/* Hero */}
                <View style={styles.hero}>
                    <Text style={[styles.eyebrow, { color: accentColor }]}>{eyebrow}</Text>

                    <View
                        style={[
                            styles.heroInner,
                            {
                                flexDirection: isWide ? 'row' : 'column',
                                alignItems: isWide ? 'flex-start' : 'center',
                            },
                        ]}
                    >
                        {/* Floating icon illustration */}
                        <Animated.View
                            style={[
                                styles.iconBubble,
                                { backgroundColor: accentColor + '12', borderColor: accentColor + '25' },
                                { transform: [{ translateY: floatAnim }, { scale: pulseAnim }] },
                            ]}
                        >
                            <View style={[styles.iconInner, { backgroundColor: accentColor + '18' }]}>
                                <MaterialCommunityIcons name={icon as any} size={52} color={accentColor} />
                            </View>

                            {/* Decorative orbiting dots */}
                            <View style={[styles.orbitDot, styles.orbitDot1, { backgroundColor: accentColor + '50' }]} />
                            <View style={[styles.orbitDot, styles.orbitDot2, { backgroundColor: accentColor + '35' }]} />
                            <View style={[styles.orbitDot, styles.orbitDot3, { backgroundColor: accentColor + '25' }]} />
                        </Animated.View>

                        <View
                            style={[
                                styles.heroText,
                                {
                                    alignItems: isWide ? 'flex-start' : 'center',
                                    flex: isWide ? 1 : undefined,
                                    width: isWide ? undefined : '100%',
                                },
                            ]}
                        >
                            <View style={[styles.badge, { backgroundColor: accentColor + '12', borderColor: accentColor + '30' }]}>
                                <MaterialCommunityIcons name="hammer-wrench" size={13} color={accentColor} />
                                <Text style={[styles.badgeText, { color: accentColor }]}>Under Construction</Text>
                            </View>
                            <h1
                                style={{
                                    fontSize: isWide ? 36 : 28,
                                    fontWeight: heroTitleStyle.fontWeight as React.CSSProperties['fontWeight'],
                                    color: heroTitleStyle.color as string,
                                    lineHeight: `${isWide ? 44 : 36}px`,
                                    margin: `0 0 ${heroTitleStyle.marginBottom ?? 14}px 0`,
                                }}
                            >
                                {title}
                            </h1>
                            <Text style={styles.heroSubtitle}>{subtitle}</Text>
                        </View>
                    </View>
                </View>

                {/* Progress indicator strip */}
                <View style={styles.progressStrip}>
                    <View style={styles.progressLeft}>
                        <MaterialCommunityIcons name="progress-wrench" size={16} color="#64748B" />
                        <Text style={styles.progressLabel}>Page in progress</Text>
                    </View>
                    <View style={styles.progressBarTrack}>
                        <View style={[styles.progressBarFill, { backgroundColor: accentColor, width: '35%' }]} />
                    </View>
                    <Text style={styles.progressPct}>35%</Text>
                </View>

                {/* Feature preview cards */}
                <View style={styles.section}>
                    <Text style={styles.sectionLabel}>What to expect</Text>
                    <View
                        style={[
                            styles.cardGrid,
                            isWide && styles.cardGridWide,
                        ]}
                    >
                        {features.map((f) => (
                            <View key={f.title} style={[styles.card, isWide && styles.cardWide]}>
                                <View style={[styles.cardIconWrap, { backgroundColor: accentColor + '10' }]}>
                                    <MaterialCommunityIcons name={f.icon as any} size={22} color={accentColor} />
                                </View>
                                <Text style={styles.cardTitle}>{f.title}</Text>
                                <Text style={styles.cardDescription}>{f.description}</Text>
                            </View>
                        ))}
                    </View>
                </View>

                {/* Contact callout */}
                <View style={[styles.callout, { borderLeftColor: accentColor }]}>
                    <MaterialCommunityIcons name="lightbulb-on-outline" size={20} color={accentColor} style={{ marginBottom: 8 }} />
                    <Text style={styles.calloutTitle}>
                        {contactPrompt ?? 'Have a suggestion or need something urgently?'}
                    </Text>
                    <Text style={styles.calloutBody}>
                        Reach out to us directly and we will be happy to help in the meantime.
                    </Text>
                    {contactEmail && (
                        <View style={styles.emailPill}>
                            <MaterialCommunityIcons name="email-outline" size={15} color={accentColor} />
                            <Text style={[styles.emailText, { color: accentColor }]}>{contactEmail}</Text>
                        </View>
                    )}
                    {!contactEmail && (
                        <View style={styles.emailPill}>
                            <MaterialCommunityIcons name="email-outline" size={15} color={accentColor} />
                            <Text style={[styles.emailText, { color: accentColor }]}>hello@snapcamp.ai</Text>
                        </View>
                    )}
                </View>

            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    page: {
        backgroundColor: '#FFFFFF',
    },
    pageContent: {
        paddingTop: 36,
        paddingBottom: 80,
    },
    container: {
        width: '100%',
        maxWidth: 960,
        alignSelf: 'center',
    },

    // Hero
    hero: {
        paddingBottom: 36,
        borderBottomWidth: 1,
        borderBottomColor: '#E2E8F0',
        marginBottom: 28,
    },
    eyebrow: {
        fontSize: 13,
        fontWeight: '700',
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        marginBottom: 24,
    },
    heroInner: {
        gap: 40,
    },
    iconBubble: {
        width: 160,
        height: 160,
        borderRadius: 80,
        borderWidth: 2,
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        position: 'relative',
    },
    iconInner: {
        width: 110,
        height: 110,
        borderRadius: 55,
        alignItems: 'center',
        justifyContent: 'center',
    },
    orbitDot: {
        position: 'absolute',
        borderRadius: 99,
    },
    orbitDot1: {
        width: 12,
        height: 12,
        top: 10,
        right: 18,
    },
    orbitDot2: {
        width: 8,
        height: 8,
        bottom: 16,
        left: 14,
    },
    orbitDot3: {
        width: 6,
        height: 6,
        top: 32,
        left: 8,
    },
    heroText: {
        display: 'flex',
        flexDirection: 'column',
    },
    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        alignSelf: 'flex-start',
        borderWidth: 1,
        borderRadius: 20,
        paddingHorizontal: 10,
        paddingVertical: 5,
        marginBottom: 16,
    },
    badgeText: {
        fontSize: 12,
        fontWeight: '600',
        letterSpacing: 0.3,
    },
    heroTitle: {
        fontSize: 28,
        fontWeight: '700',
        color: '#0F172A',
        lineHeight: 36,
        marginBottom: 14,
    },
    heroSubtitle: {
        fontSize: 16,
        fontWeight: '400',
        color: '#475569',
        lineHeight: 26,
        maxWidth: 520,
    },

    // Progress strip
    progressStrip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        backgroundColor: '#F8FAFC',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 12,
        paddingHorizontal: 16,
        paddingVertical: 12,
        marginBottom: 40,
    },
    progressLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    progressLabel: {
        fontSize: 13,
        fontWeight: '500',
        color: '#64748B',
    },
    progressBarTrack: {
        flex: 1,
        height: 6,
        backgroundColor: '#E2E8F0',
        borderRadius: 99,
        overflow: 'hidden',
    },
    progressBarFill: {
        height: '100%',
        borderRadius: 99,
    },
    progressPct: {
        fontSize: 13,
        fontWeight: '600',
        color: '#64748B',
        minWidth: 36,
        textAlign: 'right',
    },

    // Cards
    section: {
        marginBottom: 36,
    },
    sectionLabel: {
        fontSize: 11,
        fontWeight: '700',
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        color: '#94A3B8',
        marginBottom: 16,
    },
    cardGrid: {
        flexDirection: 'column',
        gap: 16,
    },
    cardGridWide: {
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    card: {
        backgroundColor: '#F8FAFC',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 16,
        padding: 20,
    },
    cardWide: {
        flex: 1,
        minWidth: 220,
    },
    cardIconWrap: {
        width: 44,
        height: 44,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 12,
    },
    cardTitle: {
        fontSize: 15,
        fontWeight: '700',
        color: '#1E293B',
        marginBottom: 6,
    },
    cardDescription: {
        fontSize: 14,
        fontWeight: '400',
        color: '#64748B',
        lineHeight: 22,
    },

    // Callout
    callout: {
        backgroundColor: '#F8FAFC',
        borderLeftWidth: 4,
        borderRadius: 12,
        padding: 24,
    },
    calloutTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: '#1E293B',
        marginBottom: 6,
    },
    calloutBody: {
        fontSize: 14,
        fontWeight: '400',
        color: '#64748B',
        lineHeight: 22,
        marginBottom: 14,
    },
    emailPill: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        alignSelf: 'flex-start',
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 20,
        paddingHorizontal: 12,
        paddingVertical: 6,
    },
    emailText: {
        fontSize: 13,
        fontWeight: '600',
    },
});

export default UnderConstructionScreen;
